// Resolves `RussiaRequestStartCity` UUIDs against the active arrival-city
// catalog and renders each as a `shortName` chip. Shares the
// `['arrival-cities-active']` query key with the editor + China-side
// destinations picker so all three consumers hit a single fetch.

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { RussiaRequestStartCity } from '@magimp/shared';

import { useActiveArrivalCities } from '../../../catalog/index.js';

// WHY: the +N overflow keeps the chip strip on one line for the common
//   case of 1–2 start cities while still surfacing every resolved name
//   in the tooltip. The threshold is small on purpose — multi-city
//   russia requests rarely list more than 2 origins.
const MAX_INLINE_CHIPS = 2;

const chipStyle = {
  padding: '1px 6px',
  borderRadius: 'var(--r-1)',
  border: '1px solid var(--border-2)',
  background: 'var(--bg-2)',
  color: 'var(--fg-1)',
  fontSize: 11,
  lineHeight: '16px',
  whiteSpace: 'nowrap' as const,
};

export function StartCityChips({
  startCities,
  testIdPrefix,
}: {
  startCities: readonly RussiaRequestStartCity[];
  testIdPrefix: string;
}) {
  const { t } = useTranslation('russiaRequests');
  // WHY: when the row has no start-cities the chip strip is empty,
  //   so skip the catalog fetch entirely. Keeps MSW handler sets in
  //   existing list/drawer tests minimal — they only need the
  //   `/api/arrival-cities` mock when a row actually carries cities.
  const citiesQuery = useActiveArrivalCities({ enabled: startCities.length > 0 });
  const byId = useMemo(
    () => new Map((citiesQuery.data ?? []).map((c) => [c.id, c])),
    [citiesQuery.data],
  );

  if (startCities.length === 0) {
    return (
      <span data-testid={`${testIdPrefix}-empty`} style={{ color: 'var(--fg-3)', fontSize: 12 }}>
        {t('startCities.empty')}
      </span>
    );
  }

  const ordered = [...startCities].sort((a, b) => a.sortOrder - b.sortOrder);
  const visible = ordered.slice(0, MAX_INLINE_CHIPS);
  const overflow = ordered.slice(MAX_INLINE_CHIPS);
  const overflowTitle = overflow
    .map((sc) => byId.get(sc.arrivalCityId)?.name ?? sc.arrivalCityId)
    .join(', ');

  return (
    <span
      data-testid={testIdPrefix}
      style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}
    >
      {visible.map((sc) => {
        const city = byId.get(sc.arrivalCityId);
        const label = city?.shortName ?? sc.arrivalCityId.slice(0, 6);
        const title = city?.name ?? sc.arrivalCityId;
        return (
          <span
            key={sc.arrivalCityId}
            title={title}
            data-testid={`${testIdPrefix}-chip-${sc.arrivalCityId}`}
            style={chipStyle}
          >
            {label}
          </span>
        );
      })}
      {overflow.length > 0 && (
        <span title={overflowTitle} data-testid={`${testIdPrefix}-overflow`} style={chipStyle}>
          {t('startCities.more', { count: overflow.length })}
        </span>
      )}
    </span>
  );
}
